import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, Wine, Clock, User, MapPin, Play, Mail, Check } from 'lucide-react';
import { preparedTastingApi } from '@/api/services';
import { useTastingStore } from '@/stores/tastingStore';
import TastingLayout from '@/components/layout/TastingLayout';

export default function PreparedTastingPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { setSession, setSpirits } = useTastingStore();
  const [isStarting, setIsStarting] = useState(false);
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  // Fetch prepared tasting data
  const { data: preparedTasting, isLoading, error } = useQuery({
    queryKey: ['preparedTasting', uuid],
    queryFn: () => preparedTastingApi.get(uuid!),
    enabled: !!uuid,
    retry: false,
  });

  // If tasting is already started and has session_uuid, redirect to session
  useEffect(() => {
    if (preparedTasting?.status === 'started' && preparedTasting.session_uuid) {
      navigate(`/session/${preparedTasting.session_uuid}`, { replace: true });
    }
  }, [preparedTasting, navigate]);

  // Start session mutation
  const startMutation = useMutation({
    mutationFn: (data: { email?: string; marketingConsent?: boolean }) =>
      preparedTastingApi.start(uuid!, data.email, data.marketingConsent),
    onSuccess: (session) => {
      setSession(session);
      if (session.spirits && session.spirits.length > 0) {
        setSpirits(session.spirits);
      }
      navigate(`/session/${session.uuid}`);
    },
    onError: (error) => {
      console.error('Failed to start tasting:', error);
      setIsStarting(false);
    },
  });

  const handleStart = (withEmail: boolean = false) => {
    setIsStarting(true);
    const data = withEmail && email
      ? { email, marketingConsent }
      : {};
    startMutation.mutate(data);
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Check if tasting has expired
  const isExpired = preparedTasting && new Date(preparedTasting.expires_at) < new Date();
  const isAlreadyStarted = preparedTasting?.status === 'started' && !preparedTasting.session_uuid;
  const isCompleted = preparedTasting?.status === 'completed';

  if (isLoading) {
    return (
      <TastingLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </TastingLayout>
    );
  }

  if (error || !preparedTasting) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <AlertCircle className="w-16 h-16 text-danger-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            Tasting nicht gefunden
          </h1>
          <p className="text-dark-400 max-w-md">
            Das vorbereitete Tasting wurde nicht gefunden oder ist abgelaufen.
          </p>
        </div>
      </TastingLayout>
    );
  }

  if (isExpired) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <Clock className="w-16 h-16 text-warning-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            Tasting abgelaufen
          </h1>
          <p className="text-dark-400 max-w-md">
            Dieses Tasting ist leider abgelaufen. Bitte wenden Sie sich an das Personal.
          </p>
        </div>
      </TastingLayout>
    );
  }

  if (isAlreadyStarted || isCompleted) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <AlertCircle className="w-16 h-16 text-warning-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            {isCompleted ? 'Tasting abgeschlossen' : 'Tasting bereits gestartet'}
          </h1>
          <p className="text-dark-400 max-w-md">
            {isCompleted
              ? 'Dieses Tasting wurde bereits abgeschlossen.'
              : 'Dieses Tasting wurde bereits gestartet. Bitte wenden Sie sich an das Personal für ein neues Tasting.'}
          </p>
        </div>
      </TastingLayout>
    );
  }

  return (
    <TastingLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-display font-bold text-dark-100 mb-2">
            Willkommen zum Tasting
          </h1>
          {preparedTasting.template_name && (
            <p className="text-xl text-primary-400">{preparedTasting.template_name}</p>
          )}
        </motion.div>

        {/* Guest Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-dark-700"
        >
          <div className="grid grid-cols-2 gap-4">
            {preparedTasting.guest_name && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-xs text-dark-400">Gast</p>
                  <p className="text-dark-100 font-medium">{preparedTasting.guest_name}</p>
                </div>
              </div>
            )}
            {preparedTasting.table_number && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-xs text-dark-400">Tisch</p>
                  <p className="text-dark-100 font-medium">{preparedTasting.table_number}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Spirits List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-dark-700"
        >
          <h2 className="text-lg font-display font-semibold text-dark-100 mb-4 flex items-center gap-2">
            <Wine className="w-5 h-5 text-primary-400" />
            Ihre Spirituosen ({preparedTasting.spirit_count})
          </h2>
          <div className="space-y-3">
            {preparedTasting.spirits.map((spirit, index) => (
              <div
                key={spirit.id}
                className="flex items-center gap-4 p-3 bg-dark-900/50 rounded-xl"
              >
                <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold text-sm">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-dark-100 font-medium">{spirit.spirit_name}</p>
                  {spirit.spirit_brand && (
                    <p className="text-sm text-dark-400">{spirit.spirit_brand}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Start Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          {/* Anonymous Start Button */}
          {!showEmailInput && (
            <button
              onClick={() => handleStart(false)}
              disabled={isStarting}
              className="w-full py-4 px-6 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isStarting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Tasting wird gestartet...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Anonym starten
                </>
              )}
            </button>
          )}

          {/* Divider */}
          {!showEmailInput && (
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-dark-700" />
              <span className="text-dark-400 text-sm">oder</span>
              <div className="flex-1 h-px bg-dark-700" />
            </div>
          )}

          {/* Email Option */}
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              disabled={isStarting}
              className="w-full py-4 px-6 bg-dark-800 border border-dark-600 text-dark-100 font-semibold rounded-xl hover:bg-dark-700 hover:border-dark-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <Mail className="w-5 h-5 text-primary-400" />
              Mit E-Mail starten
            </button>
          ) : (
            <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-dark-300 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="w-full px-4 py-3 bg-dark-900 border border-dark-600 rounded-xl text-dark-100 placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div
                  onClick={() => setMarketingConsent(!marketingConsent)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all mt-0.5 ${
                    marketingConsent
                      ? 'bg-primary-500 border-primary-500'
                      : 'border-dark-500 group-hover:border-dark-400'
                  }`}
                >
                  {marketingConsent && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-dark-300">
                  Ja, ich möchte über neue Tastings und Angebote informiert werden
                </span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowEmailInput(false);
                    setEmail('');
                    setMarketingConsent(false);
                  }}
                  disabled={isStarting}
                  className="flex-1 py-3 px-4 bg-dark-700 text-dark-200 font-medium rounded-xl hover:bg-dark-600 transition-all disabled:opacity-50"
                >
                  Zurück
                </button>
                <button
                  onClick={() => handleStart(true)}
                  disabled={isStarting || !isValidEmail(email)}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starten...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Starten
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <p className="text-center text-dark-400 text-sm">
            Durch das Starten beginnt Ihr persönliches Tasting-Erlebnis
          </p>
        </motion.div>
      </div>
    </TastingLayout>
  );
}
